import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'button',
    'buttonWaiting',
    'buttonDefault',
    'message',
    'checkbox',
    'picture',
    'radio',
  ];

  connect() {
    this.initializeParentChildRelationships();
    this.syncInitialState();
  }

  // Initialize parent-child relationships
  initializeParentChildRelationships() {
    // These maps will help us track the parent-child relationships
    this.parentChildMap = {}; // parent -> [child1, child2, ...]
    this.childParentMap = {}; // child -> parent

    // Instead of hardcoding, discover parent-child relationships from the DOM
    this.checkboxTargets.forEach((checkbox) => {
      // Parent checkboxes
      if (checkbox.dataset.isParent === 'true') {
        const parentKey = checkbox.name;

        // Initialize the parent's children array
        if (!this.parentChildMap[parentKey]) {
          this.parentChildMap[parentKey] = [];
        }
      }

      // Child checkboxes
      if (checkbox.dataset.isChild === 'true' && checkbox.dataset.parentKey) {
        const childKey = checkbox.name;
        const parentKey = checkbox.dataset.parentKey;

        // Add to child-parent map
        this.childParentMap[childKey] = parentKey;

        // Add to parent-child map
        if (!this.parentChildMap[parentKey]) {
          this.parentChildMap[parentKey] = [];
        }

        if (!this.parentChildMap[parentKey].includes(childKey)) {
          this.parentChildMap[parentKey].push(childKey);
        }
      }
    });
  }

  // Initial state synchronization
  syncInitialState() {
    // Build the selected content state based on checkbox state
    this.selectedContent = {};

    this.checkboxTargets.forEach((checkbox) => {
      if (checkbox.name !== 'all') {
        this.selectedContent[checkbox.name] = checkbox.checked;
      }
    });

    // Ensure parent-child consistency in initial state
    Object.keys(this.parentChildMap).forEach((parentKey) => {
      this.syncParentAndChildren(parentKey);
    });

    // Update the "all" checkbox state
    this.updateAllCheckbox();
  }

  // Handle when a parent checkbox is clicked
  toggleParentContent(event) {
    const parentKey = event.target.name;
    const isChecked = event.target.checked;

    // Update the parent's selected state
    this.selectedContent[parentKey] = isChecked;

    // Only update children that specifically belong to this parent
    const childKeys = this.parentChildMap[parentKey] || [];

    childKeys.forEach((childKey) => {
      // Find all checkboxes with this childKey but only update those that belong to this parent
      this.checkboxTargets.forEach((checkbox) => {
        if (
          checkbox.name === childKey &&
          checkbox.dataset.isChild === 'true' &&
          checkbox.dataset.parentKey === parentKey
        ) {
          if (!checkbox.disabled) {
            // Update the child's state to match the parent
            checkbox.checked = isChecked;
            this.selectedContent[childKey] = isChecked;
          }
        }
      });
    });

    // Handle one-pager mutual exclusivity
    this.handleOnePagerMutualExclusivity(parentKey);

    // Update the "all" checkbox state
    this.updateAllCheckbox();

    this.togglePicture(this.selectedContent['title_page']);
    this.buttonTarget.disabled = this.selectedContentCount() === 0;
  }

  // Handle when a child checkbox is clicked
  toggleChildContent(event) {
    const childKey = event.target.name;
    const isChecked = event.target.checked;

    // Get the parent key from the data attribute
    const parentKey = event.target.dataset.parentKey;

    // Update the child's selected state
    this.selectedContent[childKey] = isChecked;

    // Sync the parent's state based on children
    if (parentKey) {
      this.syncParentBasedOnChildren(parentKey);
    }

    // Handle one-pager mutual exclusivity
    this.handleOnePagerMutualExclusivity(childKey);

    // Update the "all" checkbox state
    this.updateAllCheckbox();

    this.togglePicture(this.selectedContent['title_page']);
    this.buttonTarget.disabled = this.selectedContentCount() === 0;
  }

  // Regular toggle for non-parent/child checkboxes
  toggleContent(event) {
    const key = event.target.name;
    const isChecked = event.target.checked;

    // Update selected state
    this.selectedContent[key] = isChecked;

    // Handle one-pager mutual exclusivity
    this.handleOnePagerMutualExclusivity(key);

    // Update the "all" checkbox state
    this.updateAllCheckbox();

    this.togglePicture(this.selectedContent['title_page']);
    this.buttonTarget.disabled = this.selectedContentCount() === 0;
  }

  // Handle toggle all checkbox
  toggleContentAll(event) {
    const isChecked = event.target.checked;

    // Update all checkboxes except "one_pager" and its children
    // Skip disabled checkboxes (locked content)
    this.checkboxTargets.forEach((checkbox) => {
      if (
        !checkbox.classList.contains('hidden') &&
        !checkbox.disabled &&
        checkbox.name !== 'all' &&
        checkbox.name !== 'one_pager' &&
        !this.isChildOf('one_pager', checkbox.name)
      ) {
        checkbox.checked = isChecked;
        this.selectedContent[checkbox.name] = isChecked;
      }
    });

    // Uncheck one_pager and its children
    this.checkboxTargets.forEach((checkbox) => {
      if (
        checkbox.name === 'one_pager' ||
        this.isChildOf('one_pager', checkbox.name)
      ) {
        checkbox.checked = false;
        this.selectedContent[checkbox.name] = false;
      }
    });

    this.togglePicture(this.selectedContent['title_page']);
    this.buttonTarget.disabled = this.selectedContentCount() === 0;
  }

  // Helper to sync a parent's state based on its children
  syncParentBasedOnChildren(parentKey) {
    if (!parentKey) return;

    const parentCheckbox = this.findCheckbox(parentKey);
    if (!parentCheckbox) return;

    const childKeys = this.parentChildMap[parentKey] || [];
    if (childKeys.length === 0) return;

    // Get only child checkboxes that belong to this specific parent
    const childCheckboxes = [];

    this.checkboxTargets.forEach((checkbox) => {
      if (
        checkbox.dataset.isChild === 'true' &&
        checkbox.dataset.parentKey === parentKey &&
        childKeys.includes(checkbox.name)
      ) {
        childCheckboxes.push(checkbox);
      }
    });

    if (childCheckboxes.length === 0) return;

    // Parent should be checked only if at least one child is checked
    const anyChildChecked = childCheckboxes.some(
      (checkbox) => checkbox.checked,
    );

    // Update parent state if necessary
    if (parentCheckbox.checked !== anyChildChecked) {
      parentCheckbox.checked = anyChildChecked;
      this.selectedContent[parentKey] = anyChildChecked;
    }
  }

  // Helper to sync parent and all children
  syncParentAndChildren(parentKey) {
    const parentCheckbox = this.findCheckbox(parentKey);
    if (!parentCheckbox) return;

    const childKeys = this.parentChildMap[parentKey] || [];
    if (childKeys.length === 0) return;

    // Get only child checkboxes that belong to this specific parent
    const childCheckboxes = [];

    this.checkboxTargets.forEach((checkbox) => {
      if (
        checkbox.dataset.isChild === 'true' &&
        checkbox.dataset.parentKey === parentKey &&
        childKeys.includes(checkbox.name)
      ) {
        childCheckboxes.push(checkbox);
      }
    });

    if (childCheckboxes.length === 0) return;

    // Case 1: If parent is checked, all children should be checked
    if (parentCheckbox.checked) {
      childCheckboxes.forEach((childCheckbox) => {
        if (!childCheckbox.disabled && !childCheckbox.checked) {
          childCheckbox.checked = true;
          this.selectedContent[childCheckbox.name] = true;
        }
      });
    }
    // Case 2: If any child is checked, parent should be checked
    else {
      const anyChildChecked = childCheckboxes.some(
        (checkbox) => checkbox.checked,
      );
      if (anyChildChecked) {
        parentCheckbox.checked = true;
        this.selectedContent[parentKey] = true;
      }
    }
  }

  // Helper to handle one_pager mutual exclusivity
  handleOnePagerMutualExclusivity(key) {
    // If one_pager is selected, unselect everything else
    if (
      (key === 'one_pager' || this.isChildOf('one_pager', key)) &&
      this.isSelected(key)
    ) {
      this.checkboxTargets.forEach((checkbox) => {
        if (
          checkbox.name !== 'one_pager' &&
          !this.isChildOf('one_pager', checkbox.name)
        ) {
          checkbox.checked = false;
          if (checkbox.name !== 'all')
            this.selectedContent[checkbox.name] = false;
        }
      });
    }
    // If something else is selected, unselect one_pager
    else if (
      key !== 'one_pager' &&
      !this.isChildOf('one_pager', key) &&
      this.isSelected(key)
    ) {
      this.checkboxTargets.forEach((checkbox) => {
        if (
          checkbox.name === 'one_pager' ||
          this.isChildOf('one_pager', checkbox.name)
        ) {
          checkbox.checked = false;
          if (checkbox.name !== 'all')
            this.selectedContent[checkbox.name] = false;
        }
      });
    }
  }

  // Helper to update the "all" checkbox state
  updateAllCheckbox() {
    const allCheckbox = this.findCheckbox('all');
    if (!allCheckbox) return;

    // Get all content checkboxes that count toward "all" being checked
    const contentCheckboxes = this.getContentCheckboxes();

    // "All" should be checked when all content elements are checked
    const allChecked =
      contentCheckboxes.length > 0 &&
      contentCheckboxes.every((checkbox) => checkbox.checked);

    // Update the all checkbox
    allCheckbox.checked = allChecked;
  }

  // Helper to get all content checkboxes (top-level and with parents)
  getContentCheckboxes() {
    // Get all checkboxes except:
    // - hidden checkboxes
    // - the "all" checkbox
    // - one_pager and its children
    // This includes both top-level checkboxes (no parent) and parent checkboxes
    return this.checkboxTargets.filter(
      (checkbox) =>
        !checkbox.classList.contains('hidden') &&
        checkbox.name !== 'all' &&
        checkbox.name !== 'one_pager' &&
        !this.isChildOf('one_pager', checkbox.name) &&
        !checkbox.dataset.isChild, // Exclude children as parents represent them
    );
  }

  // Helper to check if a key is a child of a specific parent
  isChildOf(parentKey, childKey) {
    return this.childParentMap[childKey] === parentKey;
  }

  // Helper to find a checkbox by name
  findCheckbox(name) {
    return this.checkboxTargets.find((checkbox) => checkbox.name === name);
  }

  // Check if a key is selected
  isSelected(key) {
    return this.selectedContent[key] === true;
  }

  // Count selected content
  selectedContentCount() {
    const keys = Object.keys(this.selectedContent);
    const selected = keys.filter((key) => this.isSelected(key));
    return selected.length;
  }

  // Toggle the picture section
  togglePicture(enabled) {
    this.pictureTarget.classList.toggle('hidden', !enabled);
  }

  // Handle download button click
  download(event) {
    event.preventDefault();

    this.abortController = new AbortController();
    this.signal = this.abortController.signal;
    this.start();

    fetch(this.urlWithParams(), { method: 'get', signal: this.signal })
      .then((response) => {
        this.stop();

        if (response.ok) {
          const filename = this.filename(response);

          return response.blob().then((blob) => {
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.setAttribute('download', filename);
            a.click();

            // Close dropdown
            this.dropdownController.toggle();
          });
        }
        throw Error(response.statusText);
      })
      .catch((error) => {
        this.stop(error);
      });
  }

  // Build URL with parameters based on selected checkboxes
  urlWithParams() {
    let result = this.buttonTarget.dataset.href;

    // Check if any content is selected
    const hasAnyContentSelected = Object.keys(this.selectedContent).some(
      (key) => this.isSelected(key),
    );

    // If no content is selected, ensure title_page is set to true
    if (!hasAnyContentSelected) {
      if (result.includes('?')) result += '&';
      else result += '?';
      result += 'title_page=true';
    } else {
      Object.keys(this.selectedContent).forEach((key) => {
        if (result.includes('?')) result += '&';
        else result += '?';

        if (this.isSelected(key)) {
          // Send all selected items as key=true for explicit selection
          result += `${key}=true`;
        } else {
          // Add non-selected content as key=false
          result += `${key}=false`;
        }
      });
    }

    // Add picture parameter
    if (result.includes('?')) result += '&';
    else result += '?';
    result += `picture=${this.selectedPicture || 'generic'}`;

    return result;
  }

  // Parse filename from content disposition header
  filename(response) {
    const disposition = response.headers.get('Content-Disposition');

    if (disposition?.includes('attachment')) {
      const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
      const matches = filenameRegex.exec(disposition);
      if (matches != null && matches[1]) {
        return matches[1].replace(/['"]/g, '');
      }
    }

    return '';
  }

  // Abort download
  abort() {
    if (this.abortController) this.abortController.abort();
  }

  // UI state when starting download
  start() {
    this.buttonTarget.disabled = true;
    this.buttonWaitingTarget.classList.remove('hidden');
    this.buttonDefaultTarget.classList.add('hidden');
    this.removeError();
  }

  // UI state when download finishes
  stop(error) {
    this.buttonTarget.disabled = false;
    this.buttonWaitingTarget.classList.add('hidden');
    this.buttonDefaultTarget.classList.remove('hidden');

    if (error) this.showError(error);
  }

  // Display error message
  showError(message) {
    this.messageTarget.classList.remove('hidden');
    this.messageTarget.innerText = message;
  }

  // Hide error message
  removeError() {
    this.messageTarget.classList.add('hidden');
    this.messageTarget.innerText = '';
  }

  // Handle picture selection
  selectPicture(event, value) {
    this.selectedPicture = value || event.target.value;
  }

  // Handle user picture selection
  selectUserPicture(event) {
    this.radioTargets.find((el) => el.value == 'user').checked = true;
    this.selectPicture(event, 'user');
  }

  // Get dropdown controller
  get dropdownController() {
    return this.application.getControllerForElementAndIdentifier(
      this.element,
      'dropdown',
    );
  }
}
